<script setup lang="ts">
import { EvaluationRange } from '@/core/domain/resources/enums/EvaluationRange.ts'
import { computed, type Ref, ref, inject } from 'vue'
import { default as PPopover } from 'primevue/popover'
import type { DashboardScoreModuleGrain } from '@/core/domain/dashboard/DashboardScoreModuleGrain.ts'

const props = defineProps<{
  grainEvalRange: EvaluationRange.Values | null
  grain?: DashboardScoreModuleGrain
}>()

// Use provide/inject pattern to avoid recreating auth store for each component
const authInfo = inject<{ isAdmin: boolean; isEduEngineer: boolean }>('authInfo', {
  isAdmin: false,
  isEduEngineer: false
})

const tooltip: Ref<any | null> = ref(null)

// Only show popover if grain exists
const shouldShowTooltip = computed(() => {
  return props.grain !== undefined
})

const togglePopover = (event: Event) => {
  if (shouldShowTooltip.value && tooltip.value) {
    tooltip.value.toggle(event)
  }
}

// Pre-compute grain score history for performance
const lastScore = computed(() => {
  if (!props.grain?.postQuizScoreScaledHistory?.length) return null
  const history = props.grain.postQuizScoreScaledHistory
  return Math.round(history[history.length - 1] * 100)
})
</script>

<template>
  <div
    class="border-surface-200 relative flex h-7 w-7 cursor-pointer items-center justify-center border bg-white"
    @mouseenter="togglePopover"
    @mouseleave="togglePopover"
  >
    <div
      v-if="grainEvalRange"
      class="flex h-full w-full items-center justify-center"
      :class="{
        'bg-green-300': grainEvalRange === EvaluationRange.Values.FORCE,
        'bg-yellow-300': grainEvalRange === EvaluationRange.Values.EFFORT_A_FAIRE,
        'bg-red-300': grainEvalRange === EvaluationRange.Values.DEFI
      }"
    >
      <i
        :class="{
          'pi pi-check': grainEvalRange === EvaluationRange.Values.FORCE,
          'pb-1 text-3xl font-light': grainEvalRange === EvaluationRange.Values.EFFORT_A_FAIRE,
          'pi pi-times': grainEvalRange === EvaluationRange.Values.DEFI
        }"
      >
        <template v-if="grainEvalRange === EvaluationRange.Values.EFFORT_A_FAIRE"> ~ </template>
      </i>
    </div>
    <p-popover ref="tooltip">
      <template v-if="grain">
        <div class="p-3">
          <p class="mb-2 text-sm font-bold">{{ grain.learningGrainTitle }}</p>
          <template v-if="grainEvalRange">
            <p class="text-xs">Tentatives : {{ grain.postQuizCountAttempt }}</p>
            <p class="text-xs">Dernier score : {{ lastScore }}%</p>
            <template
              v-if="(authInfo.isAdmin || authInfo.isEduEngineer) && grain.postQuizVersionNumber"
            >
              <p class="text-xs">Version du quiz : {{ grain.postQuizVersionNumber }}</p>
            </template>
          </template>
          <template v-else>
            <p class="text-xs italic">Ce grain n'a pas encore été évalué</p>
          </template>
        </div>
      </template>
    </p-popover>
  </div>
</template>
